import { useTranslation } from "react-i18next";
import { Building2, CalendarOff, CalendarPlus, House } from "lucide-react";
import { useAudio } from "../../../../../lib/use-audio";
import { Tile } from "../../../components/Tile";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { useCheckInSend } from "../context";

interface NoAppointmentFoundStepProps {
  onBookAppointment: () => void;
}

interface NoAppointmentFoundUnavailableStepProps {
  onComplete: () => void;
}

/**
 * No appointment found step: book a new appointment, or get help from the
 * reception for a patient who believes they have one (KC-22).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <NoAppointmentFoundStep onBookAppointment={() => undefined} />
 * ```
 */
export function NoAppointmentFoundStep({
  onBookAppointment,
}: NoAppointmentFoundStepProps) {
  const { t } = useTranslation();
  const send = useCheckInSend();

  // Play audio on mount
  useAudio("checkIn-noAppointmentFoundBookable");

  return (
    <KioskStep
      width="default"
      title={t("self-service.check-in.no-appointment.title")}
      lead={t("self-service.check-in.no-appointment.subtitle-v2")}
    >
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
        <Tile
          tone="blue-ink"
          className="min-h-44"
          data-testid="no-appointment-book"
          name={t("self-service.check-in.no-appointment.book-v2")}
          desc={t("self-service.check-in.no-appointment.book-v2-description")}
          icon={<CalendarPlus />}
          onClick={onBookAppointment}
        />
        <Tile
          tone="paper"
          className="min-h-44"
          data-testid="no-appointment-reception"
          name={t("self-service.check-in.no-appointment.have-appointment")}
          desc={t("self-service.check-in.no-appointment.have-appointment-description")}
          icon={<Building2 />}
          onClick={() => send({ type: "RECEPTION_HELP" })}
        />
      </div>
    </KioskStep>
  );
}

/**
 * No appointment found step shown when the scheduler is disabled for kiosks:
 * one consistent message that points to the reception (KC-21).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <NoAppointmentFoundUnavailableStep onComplete={() => undefined} />
 * ```
 */
export function NoAppointmentFoundUnavailableStep({
  onComplete,
}: NoAppointmentFoundUnavailableStepProps) {
  const { t } = useTranslation();

  useAudio("checkIn-noAppointmentFoundUnavailable");

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t("self-service.check-in.no-appointment.title")}
      primaryAction={{
        label: t("self-service.check-in.staff-assist.start-over"),
        icon: House,
        onClick: onComplete,
        testId: "no-appointment-home",
      }}
    >
      <KioskStatePanel
        tone="info"
        icon={<CalendarOff />}
        title={t("self-service.check-in.no-appointment.unavailable-title")}
        body={t("self-service.check-in.no-appointment.subtitle-unavailable")}
      />
    </KioskStep>
  );
}
